import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Container,
  FormFeedback,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import Dropzone from "react-dropzone";

import Breadcrumbs from "../../../components/Common/Breadcrumb";
import SuccessPopup from "../../../components/SuccessPopup";
import ErrorPopup from "../../../components/ErrorPopup";
import {
  updatePvsCategory,
  resetPvsCategoryState,
  fetchPvsCategoryDetails,
} from "../../../redux/actions/pvs";

function EditCategory() {
  document.title = "Edit PVS Category | PVS | Admin Dashboard";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enc_pvs_cat_id } = useParams();

  const [selectedIconFile, setSelectedIconFile] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const updatePvsCategoryScreen = useSelector(
    (state) => state.screens.pvs.updatePvsCategory
  );
  const fetchPvsReducer = useSelector((state) => state.pvs);

  // Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      category: "",
      description: "",
      slug: "",
      count: 0,
      icon: null, // This will hold the icon file
    },
    validationSchema: Yup.object({
      category: Yup.string().required("Please enter category"),
      description: Yup.string(),
    }),
    onSubmit: (values) => {
      // Prepare form data for submission
      const formData = new FormData();
      formData.append("category", values.category);
      formData.append("description", values.description || "");
      formData.append("enc_pvs_cat_id", enc_pvs_cat_id);
      if (selectedIconFile) formData.append("pvs_category_icon", selectedIconFile);

      dispatch(updatePvsCategory(formData, enc_pvs_cat_id));
    },
  });

  // Handle Redux update success/error
  useEffect(() => {
    if (updatePvsCategoryScreen.success) {
      setSuccessOpen(true);
    } else if (updatePvsCategoryScreen.error) {
      setErrorMsg(updatePvsCategoryScreen.message || "Something went wrong");
      setErrorOpen(true);
    }
  }, [updatePvsCategoryScreen]);

  // Fetch category data
  useEffect(() => {
    if (!enc_pvs_cat_id) return;
    dispatch(fetchPvsCategoryDetails({ enc_pvs_cat_id }));
  }, [enc_pvs_cat_id, dispatch]);

  // Set form values from fetched data
  useEffect(() => {
    if (fetchPvsReducer?.categoryDetail) {
      const data = fetchPvsReducer.categoryDetail;
      formik.setValues({
        category: data.category || "",
        description: data.description || "",
        slug: data.slug || "",
        count: data.count || 0,
        icon: null,
      });
      if (data.icon_path) setSelectedIconFile(data.icon_path); // Show existing icon
    } else if (fetchPvsReducer?.error) {
      setErrorMsg(fetchPvsReducer.message || "Unable to fetch category data");
      setErrorOpen(true);
    }
  }, [fetchPvsReducer]);

  return (
    <React.Fragment>
      <SuccessPopup
        isOpen={successOpen}
        toggle={() => setSuccessOpen(false)}
        onConfirm={() => {
          setSuccessOpen(false);
          dispatch(resetPvsCategoryState());
          navigate("/pvs-category", { replace: true });
        }}
        message={updatePvsCategoryScreen.message}
      />
      <ErrorPopup
        isOpen={errorOpen}
        toggle={() => {
          setErrorOpen(false);
          dispatch(resetPvsCategoryState());
        }}
        message={errorMsg}
      />

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            maintitle="PVS"
            title="Category"
            breadcrumbItem="Edit Category"
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      formik.handleSubmit();
                    }}
                  >
                    <Row>
                      {/* Category */}
                      <Col md={6} className="mb-3">
                        <Label htmlFor="category">Category</Label>
                        <Input
                          id="category"
                          name="category"
                          type="text"
                          placeholder="Enter category"
                          value={formik.values.category}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.category && formik.errors.category
                          }
                        />
                        {formik.touched.category && formik.errors.category && (
                          <FormFeedback>
                            {formik.errors.category}
                          </FormFeedback>
                        )}
                      </Col>

                      {/* Category Slug */}
                      <Col md={6} className="mb-3">
                        <Label htmlFor="slug">Category Slug</Label>
                        <Input
                          id="slug"
                          name="slug"
                          placeholder="Category slug"
                          value={formik.values.slug}
                          disabled
                        />
                      </Col>

                      {/* Category Count */}
                      <Col md={6} className="mb-3">
                        <Label htmlFor="count">Category Count</Label>
                        <Input
                          id="count"
                          name="count"
                          value={formik.values.count}
                          disabled
                        />
                      </Col>

                      {/* Description */}
                      <Col md={6} className="mb-3">
                        <Label htmlFor="description">Description</Label>
                        <textarea
                          id="description"
                          name="description"
                          className={`form-control ${
                            formik.touched.description &&
                            formik.errors.description
                              ? "is-invalid"
                              : ""
                          }`}
                          rows={2}
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.description &&
                          formik.errors.description && (
                            <FormFeedback>
                              {formik.errors.description}
                            </FormFeedback>
                          )}
                      </Col>

                      {/* Icon Upload */}
                      <Col md={4} className="mb-3">
                        <Label htmlFor="icon">Category Icon</Label>
                        <Dropzone
                          onDrop={(acceptedFiles) => {
                            setSelectedIconFile(acceptedFiles[0]);
                            formik.setFieldValue("icon", acceptedFiles[0]);
                          }}
                          multiple={false}
                          accept={{ "image/*": [] }}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div className="dropzone">
                              <div
                                className="dz-message needsclick"
                                {...getRootProps()}
                              /*   style={{
                                  border: "1px dashed #ccc",
                                  padding: "20px",
                                  textAlign: "center",
                                  borderRadius: "5px",
                                }} */
                              >
                                <input {...getInputProps()} />
                                <div className="mb-3">
                                  <i className="mdi mdi-cloud-upload display-4 text-muted"></i>
                                </div>
                                <h5>Drop files here or click to upload</h5>

                                {selectedIconFile && (
                                  <div className="mt-2">
                                    <img
                                      src={
                                        typeof selectedIconFile === "string"
                                          ? selectedIconFile
                                          : URL.createObjectURL(selectedIconFile)
                                      }
                                      alt="Icon Preview"
                                      style={{
                                        maxWidth: "150px",
                                        maxHeight: "100px",
                                        borderRadius: "4px",
                                        border: "1px solid #eee",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </Dropzone>
                      </Col>
                      
                    </Row>

                    <div className="d-flex justify-content-center mt-3">
                      <Button type="submit" color="primary" size="lg" className="w-25">
                        Submit
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default EditCategory;
